import React from 'react';
import i18n from '@/i18n/config';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Autocomplete,
  AutocompleteItem,
  Chip
} from '@heroui/react';
import { RiCloseLine } from '@remixicon/react';
import { useAtomValue, useAtom } from 'jotai';
import { toast } from 'react-toastify';

import { rolesQueryAtom, rolesHydratedAtom, setUserRoles } from '@/store';
import type { RoleItem, UserListItem, Role } from '@/types';
import { roleLabel } from '@/lib/roleLabel';

interface UserRolesModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserListItem | null;
  onSuccess?: () => void;
}

export function UserRolesModal({ isOpen, onOpenChange, user, onSuccess }: UserRolesModalProps) {
  const [, setRolesHydrated] = useAtom(rolesHydratedAtom);
  const { data: rolesData, isLoading: isLoadingRoles } = useAtomValue(rolesQueryAtom);
  const [, setUserRolesAtom] = useAtom(setUserRoles);
  const [selectedRoles, setSelectedRoles] = React.useState<Role[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const roles = rolesData?.data || [];

  // Enable query when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setRolesHydrated(true);
    }
  }, [isOpen, setRolesHydrated]);

  // Initialize selected roles when user changes
  React.useEffect(() => {
    if (user && isOpen) {
      setSelectedRoles(user.roles || []);
    }
  }, [user, isOpen]);

  const handleRoleAdd = React.useCallback(
    (roleId: string) => {
      const roleItem = roles.find((r) => r.id.toString() === roleId);
      if (roleItem && !selectedRoles.find((r) => r.id === roleItem.id)) {
        // Convert RoleItem to Role
        const role: Role = {
          id: roleItem.id,
          name: roleItem.name
        };
        setSelectedRoles((prev) => [...prev, role]);
      }
    },
    [roles, selectedRoles]
  );

  const handleRoleRemove = React.useCallback((roleId: number) => {
    setSelectedRoles((prev) => prev.filter((role) => role.id !== roleId));
  }, []);

  const handleSubmit = React.useCallback(async () => {
    if (!user) return;

    try {
      setIsSubmitting(true);
      const roleIds = selectedRoles.map((role) => role.id);

      await setUserRolesAtom({ userId: user.id, roleIds });

      toast.success(i18n.t('copy.user_roles_updated_successfully'));
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(i18n.t('copy.failed_to_update_user_roles'));
    } finally {
      setIsSubmitting(false);
    }
  }, [user, selectedRoles, onSuccess, onOpenChange, setUserRolesAtom]);

  const handleClose = React.useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const getRoleColor = (roleName: string) => {
    switch (roleName) {
      case 'super_admin':
        return 'danger';
      case 'admin':
        return 'primary';
      case 'customer':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={handleClose} size="md" isDismissable={false}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              Manage Roles for {user ? `${user.first_name} ${user.last_name}`.trim() || user.email : ''}
            </ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4">
                {/* Role Autocomplete */}
                <Autocomplete
                  label={i18n.t('copy.add_role')}
                  placeholder={i18n.t('copy.search_and_select_a_role')}
                  isLoading={isLoadingRoles}
                  onSelectionChange={(key) => {
                    if (key) {
                      handleRoleAdd(key as string);
                    }
                  }}
                  onOpenChange={() => {}} // Prevent modal from closing
                >
                  {roles.map((role) => (
                    <AutocompleteItem key={role.id.toString()}>
                      <div className="flex flex-col">
                        <span className="font-medium">{roleLabel(role.name)}</span>
                        {role.description && <span className="text-small text-default-500">{role.description}</span>}
                      </div>
                    </AutocompleteItem>
                  ))}
                </Autocomplete>

                {/* Selected Roles */}
                {selectedRoles.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <span className="text-small font-medium">Selected Roles:</span>
                    <div className="flex flex-wrap gap-2">
                      {selectedRoles.map((role) => (
                        <Chip
                          key={role.id}
                          color={getRoleColor(role.name)}
                          variant="flat"
                          onClose={() => handleRoleRemove(role.id)}
                          endContent={
                            <Button
                              isIconOnly
                              size="sm"
                              variant="light"
                              className="h-4 w-4 min-w-4"
                              onPress={() => handleRoleRemove(role.id)}
                            >
                              <RiCloseLine className="h-3 w-3" />
                            </Button>
                          }
                        >
                          {roleLabel(role.name)}
                        </Chip>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose}>{i18n.t('modals.exportConfirm.cancel')}</Button>
              <Button
                color="primary"
                isLoading={isSubmitting}
                onPress={handleSubmit}
                isDisabled={selectedRoles.length === 0}
              >
                Update Roles
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
